import { z } from "zod";

import { PROFESSION_OPTIONS } from "./constants";
import { STEP_IDS } from "./flow";
import { onboardingDraftAnswersSchema } from "./schema";

/** The guided onboarding's pre-sign-in answers, held in the browser until the
 *  `about` step submits them with the user's consent.
 *
 *  localStorage, not sessionStorage: in the iOS shell the deep-link OAuth
 *  fallback leaves the app for the system browser, and iOS may evict the
 *  backgrounded webview, which drops sessionStorage. localStorage survives
 *  that, the web OAuth redirect, and the shell's native sign-in sheet.
 *
 *  The stored shape is parsed on every read. Anything that doesn't parse
 *  (an old version, tampering, a key outside the non-personal pick in
 *  `onboardingDraftAnswersSchema`) is discarded rather than trusted: the
 *  flow then resumes at the first required question. */
export const DRAFT_STORAGE_KEY = "medceptor.onboarding.draft.v1";

/** Older than this and the draft is dropped. Short on purpose: it exists to
 *  carry answers across one sign-up round trip, and EU ePrivacy guidance
 *  treats "user-input" storage as consent-exempt only while it lasts no
 *  longer than that purpose needs (WP29 Opinion 04/2012). */
export const DRAFT_TTL_MS = 3 * 24 * 60 * 60 * 1000;

const professionValues = PROFESSION_OPTIONS.map((o) => o.value);

const draftSchema = z
  .object({
    step: z.enum(STEP_IDS).optional(),
    profession: z.enum(professionValues).optional(),
    answers: onboardingDraftAnswersSchema.strict(),
    savedAt: z.number(),
  })
  .strict();
export type OnboardingDraft = z.infer<typeof draftSchema>;

/** The subset of the Web Storage API this module touches, so tests can pass
 *  a plain object and private-mode throws can be simulated. */
export type DraftStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserStorage(): DraftStorage | null {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    // Safari with storage blocked throws on the property access itself.
    return null;
  }
}

export function readDraft(
  storage: DraftStorage | null = browserStorage(),
  now: number = Date.now(),
): OnboardingDraft | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = draftSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || now - parsed.data.savedAt > DRAFT_TTL_MS) {
      storage.removeItem(DRAFT_STORAGE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

/** Persist the draft. Validates before writing so a caller can't smuggle an
 *  unpicked (personal) field into storage; returns false when it refused or
 *  storage is unavailable. The flow keeps working in memory either way. */
export function writeDraft(
  draft: Omit<OnboardingDraft, "savedAt">,
  storage: DraftStorage | null = browserStorage(),
  now: number = Date.now(),
): boolean {
  if (!storage) return false;
  const parsed = draftSchema.safeParse({ ...draft, savedAt: now });
  if (!parsed.success) return false;
  try {
    storage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(parsed.data));
    return true;
  } catch {
    // Quota exceeded / private mode.
    return false;
  }
}

export function clearDraft(storage: DraftStorage | null = browserStorage()): void {
  try {
    storage?.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    // Nothing to do: storage unavailable means nothing was stored.
  }
}
